import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportSalesByProposalSource from "hooks/organization/reports/types/useGetReportSalesByProposalSource";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableSalesByProposalSource(startDate, endDate) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportSalesByProposalSource, { data, loading, error }] =
    useGetReportSalesByProposalSource(false);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        page,
        first,
      };
      getReportSalesByProposalSource({ variables });
    },
    [startDate, endDate, getReportSalesByProposalSource]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportSalesByProposalSource?.paginatorInfo,
  });

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const columns = useMemo(() => {
    return [
      {
        Header: "Proposal Source Name",
        accessor: "s1",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposals Count",
        accessor: "pTotal",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposals Won Count",
        accessor: "proposalsWonCount",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Win Rate",
        accessor: "winRate",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Proposals Amount Sum",
        accessor: "proposalsWonAmountTotal",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Percentage of Total Organization Revenue",
        accessor: "orgRevenuePercentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportSalesByProposalSource?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    getReportSalesByProposalSource,
    pagination,
  } as const;
}
